import useBreakpoint from "@/hooks/use-breakpoint";
import {
   computeValidItemsForOption,
   updateDisabledSelection,
} from "@/utils/stagePageUtils";
import React, { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useAdminStore } from "../../store/useAdminStore";
import {
   FormValues,
   MainProduct,
   OptionTypes,
   Stage,
} from "../../types/productTypes";
import ImageGallery from "./gallery/ImageGallery";
import { useStageImages } from "./hooks/useStageImage";
import CheckboxOption from "./options/checkboxOption/CheckboxOption";
import Offer from "./options/offer/Offer";
import RadioOption from "./options/radioOption/RadioOption";
import SelectOption from "./options/selectOption/SelectOption";
import { Summary } from "./options/summary/Summary";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/use-translation";

type StagePageProps = {
   index: number;
   stage: Stage;
   nextStage?: Stage | null;
   productName: string;
   productTitle: string;
   product: MainProduct;
   checkout: (threshold: number) => void;
   goToNext: () => void;
   children?: React.ReactNode;
   priceComponent?: React.ReactNode;
   stepper?: React.ReactNode;
};

const optionComponentsMap: Record<OptionTypes, React.ElementType> = {
   radio: RadioOption,
   checkbox: CheckboxOption,
   summary: Summary,
   offer: Offer,
   select: SelectOption,
};

export default function StagePage({
   index,
   nextStage,
   stage,
   productName,
   productTitle,
   product,
   checkout,
   goToNext,
   priceComponent,
   stepper,
}: StagePageProps) {
   const { watch, setValue } = useFormContext<FormValues>();
   const watchedValues = watch();
   const { optionPairs, allLearnMore } = useAdminStore();
   const { isMobile } = useBreakpoint();
   const { translation, translateNameChange } = useTranslation();
   const mainImage = optionPairs.find(
      (pair) => stage.optionPairId && pair.id === stage.optionPairId
   )?.mainImage;

   const itemPairImages = useStageImages(
      stage,
      nextStage,
      watchedValues,
      index
   );
   const optionsRef = useRef<HTMLDivElement>(null);

   useEffect(() => {
      const el = optionsRef.current;
      if (!el) return;

      const onWheel = (e: WheelEvent) => {
         const { scrollTop, scrollHeight, clientHeight } = el;
         const delta = e.deltaY;
         const canScrollUp = delta < 0 && scrollTop > 0;
         const canScrollDown =
            delta > 0 && scrollTop + clientHeight < scrollHeight;

         if (canScrollUp || canScrollDown) {
            e.preventDefault();
            el.scrollTop += delta;
         }
      };

      window.addEventListener("wheel", onWheel, { passive: false });
      return () => window.removeEventListener("wheel", onWheel);
   }, []);

   const options = React.useMemo(() => {
      return (
         stage.options?.map((option) => {
            if (!option.id) {
               throw new Error("OptionId must be defined");
            }
            const optionIdNum = option.id;

            const validItems = computeValidItemsForOption(
               option,
               watchedValues,
               translateNameChange
            );

            const selectedDefault = validItems.find(
               (item) => item.id === option.defaultValue && !item.disabled
            );
            const fallbackDefault = validItems.find((item) => !item.disabled);
            const newDefaultId =
               selectedDefault?.id ?? fallbackDefault?.id ?? null;

            const watchedOption = watchedValues?.[optionIdNum.toString()];
            updateDisabledSelection(
               optionIdNum,
               validItems,
               watchedOption,
               setValue
            );

            const sortedItems = [...validItems].sort((a, b) => {
               if (a.disabled === b.disabled) {
                  return a.relativePos - b.relativePos;
               }
               return a.disabled ? 1 : -1;
            });

            return {
               ...option,
               items: sortedItems,
               defaultValue: newDefaultId,
            };
         }) ?? []
      );
   }, [stage.options, watchedValues, setValue, allLearnMore]);

   const renderOptionComponents = () => {
      return options.map((option, idx) => {
         const OptionComponent = optionComponentsMap[option.type];
         return (
            <OptionComponent
               key={`${option.type}-${idx}`}
               option={option}
               productName={productName}
               product={product}
               goToNext={goToNext}
               checkout={checkout}
               stage={stage}
            />
         );
      });
   };

   if (!stage.options) return null;
   return (
      <div className="flex w-full h-full flex-col lg:flex-row">
         {/* ── Left: Gallery ─────────────────────────────── */}
         <div className="flex-1 sticky top-0 z-10 bg-white">
            <ImageGallery
               itemPairImages={itemPairImages}
               stageName={stage.stageName}
               mainImage={mainImage}
               productTitle={productTitle}
            />
         </div>

         {/* ── Right: Stepper + Options + Price ────────────────────── */}
         <div className="relative w-full lg:w-1/3 flex flex-col">
            {/* Stepper at the top */}

            <div
               ref={optionsRef}
               className={cn(
                  "overflow-y-auto flex-1 pb-32 px-6 pt-6",
                  isMobile ? "pt-10" : ""
               )}
            >
               {stepper}
               {stage.type !== "offer" && stage.type !== "summary" && (
                  <h2 className="text-d-h2 mt-4 text-custom-dark">
                     {translation.configurator.title}
                  </h2>
               )}

               <div className="flex flex-col gap-4 mt-6">
                  {renderOptionComponents()}
               </div>
            </div>

            <div
               className={cn(
                  "w-full left-0",
                  isMobile ? "fixed bottom-0" : "absolute bottom-0"
               )}
            >
               {priceComponent}
            </div>
         </div>
      </div>
   );
}
